import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useHomeContext } from "./HomeContext";
import { ComparisonResponse } from "./types";
import config from "./config";


const Compare: React.FC = () => {
  const { products } = useHomeContext();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const ids = params.get("ids")?.split(",").map(Number) || [];
  const compareProducts = products.filter(p => ids.includes(p.id));

  const [comparisonResult, setComparisonResult] = useState<ComparisonResponse | null>(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchComparison = async () => {
      if (compareProducts.length >= 2) {
        setLoadingComparison(true);
        try {
          const res = await fetch(`${config.apiBaseUrl}/api/productcomparison/compare`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ productIds: ids }),
          });
          if (!res.ok) throw new Error("Failed to get comparison");
          const data = await res.json();
          setComparisonResult(data);
        } catch {
          setComparisonResult(null);
        } finally {
          setLoadingComparison(false);
        }
      } else {
        setComparisonResult(null);
      }
    };
    fetchComparison();
    // eslint-disable-next-line
  }, [ids.join(",")]);

  // Collect all unique feature names from all products
  const featureNames: string[] = comparisonResult?.products_comparison
    ? Array.from(
        new Set(
          comparisonResult.products_comparison.flatMap(prod =>
            prod.features.map(f => f.feature_name)
          )
        )
      )
    : [];

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: isMobile ? 16 : 24 }}>
      <button 
        onClick={() => navigate(-1)} 
        style={{ 
          marginBottom: 16, 
          padding: isMobile ? '8px 16px' : '12px 24px',
          backgroundColor: '#2c5aa0',
          color: '#fff',
          border: 'none',
          borderRadius: '8px',
          fontSize: '1rem',
          cursor: 'pointer'
        }}
      >
        ← Back to List
      </button>
      <h2 style={{ fontSize: isMobile ? '1.5rem' : '2rem' }}>Compare Products</h2>
      {compareProducts.length < 2 ? (
        <div>Select at least two products to compare.</div>
      ) : (
        <>
          {/* Product images and basic info at the top */}
          <div style={{ display: "flex", flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 16 : 32, marginBottom: 32 }}>
            {compareProducts.map((prod, idx) => (
              <div key={prod.id + idx} style={{ textAlign: isMobile ? 'left' : "center", flex: 1, display: 'flex', flexDirection: isMobile ? 'row' : 'column', alignItems: isMobile ? 'center' : 'stretch', gap: isMobile ? 16 : 0, padding: isMobile ? 12 : 0, backgroundColor: isMobile ? '#f9fafb' : 'transparent', borderRadius: isMobile ? 8 : 0 }}>
                <img
                  src={prod.imageUrl || ""}
                  alt={prod.name}
                  style={{
                    width: isMobile ? 80 : 120,
                    height: isMobile ? 80 : 120,
                    maxHeight: isMobile ? 80 : 120,
                    objectFit: "cover",
                    borderRadius: 8,
                    marginBottom: isMobile ? 0 : 8,
                    background: "#eee",
                    flexShrink: 0
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: isMobile ? 14 : 16 }}>{prod.name}</div>
                  <div style={{ fontSize: isMobile ? 13 : 14, color: "#555" }}>{prod.brand}</div>
                  <div style={{ fontSize: isMobile ? 13 : 14, color: "#555" }}>{prod.model}</div>
                  <div style={{ fontSize: isMobile ? 13 : 14, color: "#555", fontWeight: 600 }}>Price: ${prod.salePrice}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginBottom: 24 }}>
            {/* <h3>AI General Comment</h3> */}
            {loadingComparison ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '300px',
                fontSize: '1rem',
                color: '#666',
                padding: '40px 20px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e0e0e0'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '20px'
                }}>
                  {/* Loading Spinner */}
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #2c5aa0',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  
                  {/* Loading Text */}
                  <div style={{
                    fontSize: '1.1rem',
                    fontWeight: '500'
                  }}>
                    Loading comparison...
                  </div>
                  
                  {/* Additional Info */}
                  <div style={{
                    fontSize: '0.9rem',
                    color: '#888',
                    textAlign: 'center'
                  }}>
                    Analyzing products and generating insights
                  </div>
                  
                  {/* Animated Dots */}
                  <div style={{
                    fontSize: '1.2rem',
                    letterSpacing: '2px',
                    animation: 'dots 1.5s infinite'
                  }}>
                    • • •
                  </div>
                </div>
                
                {/* CSS Animation Styles */}
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                  
                  @keyframes dots {
                    0%, 20% { opacity: 0; }
                    50% { opacity: 1; }
                    100% { opacity: 0; }
                  }
                `}</style>
              </div>
            ) : (
              <div style={{ background: "#eef", padding: 16, borderRadius: 8 }}>
                {comparisonResult?.general_comment || "No comment available."}
              </div>
            )}
          </div>
          {comparisonResult &&
            Array.isArray(comparisonResult.products_comparison) &&
            comparisonResult.products_comparison.length > 0 && (
              <div style={{ overflowX: "auto", marginTop: 24, border: isMobile ? '1px solid #ddd' : 'none', borderRadius: 8 }}>
                <table style={{ width: "100%", minWidth: isMobile ? 600 : 'auto', borderCollapse: "collapse", background: "#fff" }}>
                  <thead>
                    <tr style={{ background: "#f3f4f6" }}>
                      <th style={{ padding: isMobile ? 6 : 8, border: "1px solid #ddd", fontSize: isMobile ? 13 : 14, position: isMobile ? 'sticky' : 'static', left: 0, background: '#f3f4f6', zIndex: 1 }}>Feature</th>
                      {comparisonResult.products_comparison.map((prod, idx) => (
                        <th key={prod.product_name + idx} style={{ padding: isMobile ? 6 : 8, border: "1px solid #ddd", fontSize: isMobile ? 13 : 14 }}>
                          {prod.product_name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {featureNames.map(feature => (
                      <tr key={feature}>
                        <td style={{ padding: isMobile ? 6 : 8, border: "1px solid #ddd", fontWeight: 600, fontSize: isMobile ? 12 : 14, position: isMobile ? 'sticky' : 'static', left: 0, background: '#fff', zIndex: 1 }}>
                          {feature}
                        </td>
                        {comparisonResult.products_comparison.map((prod, idx) => {
                          const found = prod.features.find(f => f.feature_name === feature);
                          return (
                            <td key={prod.product_name + feature + idx} style={{ padding: isMobile ? 6 : 8, border: "1px solid #ddd", fontSize: isMobile ? 12 : 14 }}>
                              {found ? found.feature_value : "-"}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </>
      )}
    </div>
  );
};

export default Compare;